/**
 * Template variables the CRM fills in itself at send time, so a field mapping for them is
 * irrelevant and an empty one is NOT a problem.
 *
 * {{{crm_unsubscribe_url}}} / {{{crm_preferences_url}}}: injectEmailPreferenceLinks
 * (_shared/emailPreferenceLinks.ts) writes signed links into the template data AFTER the field
 * mapper runs, overriding any mapped value — but ONLY when the send carries an email category
 * (an ASM group with group_id > 0). Without a category nothing is injected and the links render
 * EMPTY, so the checks warn in that case instead of staying quiet.
 *
 * KEEP IN SYNC: crm-frontend/src/utils/systemTemplateVariables.ts must stay byte-identical to
 * supabase/functions/_shared/systemTemplateVariables.ts (a vitest asserts it).
 */
export const CRM_SYSTEM_LINK_FIELDS: readonly string[] = [
  "crm_unsubscribe_url",
  "crm_preferences_url",
];

/**
 * True ONLY for the exact names the send path injects. injectEmailPreferenceLinks writes the
 * lowercase keys `crm_unsubscribe_url` / `crm_preferences_url`, and SendGrid/Handlebars variable
 * lookup is case-sensitive, so `{{{CRM_Unsubscribe_URL}}}` is NOT filled and must still warn
 * (audit PSL-1, 2026-10-06). No trimming, no case folding.
 */
export function isCrmSystemLinkField(
  field: string | null | undefined,
): boolean {
  if (!field) {
    return false;
  }
  return CRM_SYSTEM_LINK_FIELDS.includes(field);
}

/** Plain-language note shown instead of a warning when the send has an email category. */
export const CRM_SYSTEM_LINK_NOTE =
  "Filled in automatically at send time: the unsubscribe and email-preference links.";

/** Plain-language warning when the send has NO email category (the links would be empty). */
export const CRM_SYSTEM_LINK_NO_CATEGORY_WARNING =
  "The unsubscribe and email-preference links are filled in only when the flight has an email category. This flight has none, so they would be empty.";

/**
 * {{subject}}: the send path fills it from the FLIGHT's subject line (campaign_flights.email_subject).
 * send-campaign-flight / send-campaign-flight-test compute
 *   resolvedSubject = email_subject ? substituteHandlebars(email_subject, templateData) : undefined
 * and flightSendHelpers.sendOne then sets dynamic_template_data.subject = resolvedSubject (overriding
 * any field-mapper value) whenever resolvedSubject is non-empty. So a `subject` slot is filled iff the
 * flight has a subject line. Exact, case-sensitive name only: `Subject` / `SUBJECT` are NOT filled.
 */
export const FLIGHT_SUBJECT_FIELD = "subject";

export function isFlightSubjectField(
  field: string | null | undefined,
): boolean {
  return field === FLIGHT_SUBJECT_FIELD;
}

/** Plain-language note shown instead of a warning when the flight's subject line fills {{subject}}. */
export const FLIGHT_SUBJECT_NOTE =
  "Filled in automatically at send time: the subject (from the flight's subject line).";

/**
 * {{preheader}}: the send path fills it from the FLIGHT's settings (campaign_flights.metadata.preheader).
 * send-campaign-flight (both paths) and send-campaign-flight-test pass it as an `extraFields` entry
 * when truthy, and renderTemplateData (flightSendHelpers) applies extraFields AFTER the field mappings,
 * so the flight's preheader OVERRIDES any field-mapper value for the exact key `preheader`. Sequence
 * and transactional sends (process-messages, send-email) do not use flights and never set it.
 * Exact, case-sensitive name only.
 */
export const FLIGHT_PREHEADER_FIELD = "preheader";

export function isFlightPreheaderField(
  field: string | null | undefined,
): boolean {
  return field === FLIGHT_PREHEADER_FIELD;
}

/** Plain-language note when the flight's preheader setting fills {{preheader}}. */
export const FLIGHT_PREHEADER_NOTE =
  "Filled in automatically at send time: the preheader (from the flight's settings).";

/** One readable note when both the subject and the preheader come from the flight. */
export const FLIGHT_SUBJECT_AND_PREHEADER_NOTE =
  "Filled in automatically at send time: the subject (from the flight's subject line) and the preheader (from the flight's settings).";

/** Which note to show for the flight-filled fields that apply (undefined when neither does). */
export function flightFilledNote(
  subject: boolean,
  preheader: boolean,
): string | undefined {
  if (subject && preheader) {
    return FLIGHT_SUBJECT_AND_PREHEADER_NOTE;
  }
  if (subject) {
    return FLIGHT_SUBJECT_NOTE;
  }
  if (preheader) {
    return FLIGHT_PREHEADER_NOTE;
  }
  return undefined;
}

/*
 * Round 2 (audit PSUB-1 / PSUB-2, 2026-10-07): decide "filled" EXACTLY the way the send path does.
 *
 * subject: resolvedSubject = flightOverrideValue(substituteHandlebars(rawSubject, templateData)) — the send
 *   path substitutes ONCE, then drops an empty or spaces-only result (owner-approved send-path fix). So:
 *   - empty / spaces only after substitution -> NO override; the field-mapper value (if any) renders, and
 *     the email subject line falls back to the template's own subject;
 *   - other text -> override (the flight's subject wins), sent unchanged (never trimmed).
 * preheader: flightOverrideValue(flight.metadata.preheader) is passed as an extra field (no substitution),
 *   applied after the mappings; empty / spaces only -> not passed (the mapping governs).
 */

/** Same regex and semantics as flightSendHelpers.substituteHandlebars (unknown/null -> ""). */
export const MERGE_TOKEN_RE = /\{\{\{?\s*(\w+)\s*\}?\}\}/g;

export function substituteMergeFields(
  template: string,
  vars: Record<string, unknown>,
): string {
  return template.replace(MERGE_TOKEN_RE, (_m, name: string) => {
    const v = vars[name];
    if (v === undefined || v === null) {
      return "";
    }
    return String(v);
  });
}

/**
 * none        — no override (unset, empty or spaces only): the slot keeps the field-mapper value
 *               (or is empty without one);
 * filled      — overridden with real text;
 * conditional — depends on recipient data the caller doesn't have; may be empty for some recipients
 *               (then: no override, the field-mapper value is used).
 */
export type FlightFieldFill = "none" | "filled" | "conditional";

function classifyResolved(resolved: string): FlightFieldFill {
  return flightOverrideValue(resolved) === undefined ? "none" : "filled";
}

/**
 * Classify the flight subject's override for ONE substitution pass, exactly as the send path does it
 * (send: resolvedSubject = flightOverrideValue(substituteHandlebars(raw, templateData))).
 *
 * @param vars     the send-time template data the caller can know (never preview-only extras)
 * @param unknown  fields whose send-time value the caller cannot know (per-recipient data it doesn't
 *                 have). A token for a field that is neither in `vars` nor `unknown` resolves to "",
 *                 as at send time.
 * Substitution is a single pass: text inserted for a token (even if it contains "{{…}}") is never
 * substituted again — String.prototype.replace with a function does not rescan its output.
 */
export function classifyFlightSubject(
  rawSubject: string | null | undefined,
  vars: Record<string, unknown>,
  unknown: ReadonlySet<string> = new Set(),
): FlightFieldFill {
  if (!rawSubject) {
    return "none";
  }
  let unknownToken = false;
  const known = rawSubject.replace(MERGE_TOKEN_RE, (_m, name: string) => {
    if (unknown.has(name)) {
      unknownToken = true;
      return "";
    }
    const v = vars[name];
    if (v === undefined || v === null) {
      return "";
    }
    return String(v);
  });
  if (!unknownToken) {
    return classifyResolved(known);
  }
  // Unknown tokens could be empty or text. Non-blank text around them makes the result non-blank
  // whatever they resolve to; otherwise the outcome depends on the recipient.
  return known.trim() !== "" ? "filled" : "conditional";
}

export function classifyFlightPreheader(
  rawPreheader: string | null | undefined,
): FlightFieldFill {
  return classifyResolved(rawPreheader ?? "");
}

export const FLIGHT_SUBJECT_CONDITIONAL_NOTE =
  "Filled from the flight's subject line at send time; it uses merge fields that may be empty for some recipients.";

/**
 * Send-path rule (owner-approved 2026-10-07): a flight subject (after merge-field substitution) or a flight
 * preheader that is empty or SPACES ONLY is "no override" — the template's field-mapper value (and, for the
 * email subject line, the template's own default) is used, exactly as for an unset value. A value with any
 * non-space character is returned UNCHANGED (never trimmed).
 */
export function flightOverrideValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}
